import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isLandingPage = location.pathname === '/';

  // Use dark theme on landing page, light theme elsewhere
  const isDarkTheme = isLandingPage && !currentUser;

  return (
    <header className={`${isDarkTheme ? 'glass-nav' : 'bg-white border-b border-gray-200 shadow-sm'} sticky top-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className={`w-8 h-8 rounded-lg ${isDarkTheme ? 'bg-gradient-to-br from-primary-500 to-electric-500' : 'bg-gradient-to-br from-indigo-500 to-purple-500'} flex items-center justify-center`}>
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span className={`text-xl font-bold ${isDarkTheme ? 'text-white' : 'bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'}`}>
              AccessShield
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {currentUser ? (
              <>
                <Link
                  to="/dashboard"
                  className={`${
                    isActive('/dashboard')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/audit/new"
                  className={`${
                    isActive('/audit/new')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  New Audit
                </Link>
                <Link
                  to="/history"
                  className={`${
                    isActive('/history')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  History
                </Link>
                <Link
                  to="/schedule"
                  className={`${
                    isActive('/schedule')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  Schedule
                </Link>
                <Link
                  to="/subscription"
                  className={`${
                    isActive('/subscription')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  Subscription
                </Link>
                <Link
                  to="/settings"
                  className={`${
                    isActive('/settings')
                      ? 'text-indigo-600 font-semibold'
                      : 'text-gray-600 hover:text-indigo-600'
                  } transition-colors`}
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/subscription"
                  className={`${isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'} transition-colors`}
                >
                  Pricing
                </Link>
                <Link
                  to="/login"
                  className={`${isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'} transition-colors`}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${isDarkTheme ? 'bg-gradient-to-r from-primary-600 to-electric-600 hover:shadow-glow' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            className={`md:hidden p-2 rounded-lg ${isDarkTheme ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${isDarkTheme ? 'border-white/10' : 'border-gray-200'}`}>
            <nav className="flex flex-col space-y-4">
              {currentUser ? (
                <>
                  <Link
                    to="/dashboard"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/audit/new"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    New Audit
                  </Link>
                  <Link
                    to="/history"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    History
                  </Link>
                  <Link
                    to="/schedule"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Schedule
                  </Link>
                  <Link
                    to="/subscription"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Subscription
                  </Link>
                  <Link
                    to="/settings"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-left text-gray-600 hover:text-indigo-600 transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/subscription"
                    className={`${isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'} transition-colors`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/login"
                    className={`${isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'} transition-colors`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className={`${isDarkTheme ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'} transition-colors`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
